/**
 * Recovery from a stale build in a long-lived tab.
 *
 * Every module link in the left-hand nav is a lazy `loadChildren` route, and the
 * ERS app is built with the esbuild `application` builder + `outputHashing: all`,
 * so those routes arrive as `chunk-<hash>.js` fetched through a native dynamic
 * `import()`. The nginx image is replaced wholesale on every deploy and serves
 * hashed assets with `try_files $uri =404`, so the previous build's chunks stop
 * existing the moment a new one ships.
 *
 * A tab left open across a deploy (overnight, typically) is still running the old
 * `main.js`. The first click on a module link asks for a chunk that is now a 404,
 * the dynamic import rejects, and the Router reports a NavigationError — with no
 * handler, the URL never changes and nothing appears on screen. Every other
 * not-yet-loaded module link fails the same way, so the whole nav looks dead until
 * the user reloads (index.html is `no-store`, so a reload picks up the new hashes).
 *
 * Bug 32721.
 */

/** Timestamp of the last recovery reload, so a genuine 404 cannot loop. */
const RELOAD_GUARD_KEY = 'ers_stale_build_reload_at';

/** A second failure this soon after a reload is not a stale build. */
const RELOAD_GUARD_WINDOW_MS = 30_000;

/**
 * True when `error` is a failed lazy-route chunk fetch rather than an ordinary
 * navigation failure (a rejected guard, a resolver error, a bad param).
 *
 * Both wordings are covered: the esbuild/native-import phrasing the ERS build
 * produces today, and webpack's `ChunkLoadError`, which the desktop build and
 * any future builder change can still emit.
 */
export function isStaleBuildChunkError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const name = (error as { name?: unknown }).name;
  if (name === 'ChunkLoadError') {
    return true;
  }

  const message =
    typeof error === 'string'
      ? error
      : typeof (error as { message?: unknown }).message === 'string'
        ? ((error as { message: string }).message)
        : '';

  if (!message) {
    return false;
  }

  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message)
  );
}

/**
 * True when enough time has passed since the last recovery reload to try again.
 * Records the attempt as a side effect, so a chunk that 404s even on a fresh
 * build fails visibly once instead of reloading the app forever.
 */
export function shouldAttemptStaleBuildReload(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now: number = Date.now()
): boolean {
  let lastAttempt = 0;

  try {
    lastAttempt = Number(storage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    // Storage can throw in a hardened/private context; treat it as "never tried".
  }

  if (lastAttempt > 0 && now - lastAttempt < RELOAD_GUARD_WINDOW_MS) {
    return false;
  }

  try {
    storage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Without storage we cannot guard against a loop, but a single failed
    // navigation still deserves the reload that fixes the common case.
  }

  return true;
}
